import {
  Box, Typography, Chip, Stack, Alert, Divider,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  CheckCircleOutline, ErrorOutline, WarningAmber,
} from "@mui/icons-material";
import { useState, useMemo } from "react";
import { useQuery } from "convex/react";
import { useStableQuery } from "../hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { useCycleFilter } from "../cycleFilterContext";
import { DataTableShell } from "../components/table/DataTableShell";
import { CountingFilterSelect } from "../components/table/CountingFilterSelect";
import {
  DocumentCompareDrawer,
  DetailsJsonBlock,
} from "../components/viewer/DocumentCompareDrawer";

const TIME_RANGES = [
  { label: "Last 1 hour", value: "1h", ms: 60 * 60 * 1000 },
  { label: "Last 6 hours", value: "6h", ms: 6 * 60 * 60 * 1000 },
  { label: "Last 24 hours", value: "24h", ms: 24 * 60 * 60 * 1000 },
  { label: "Last 7 days", value: "7d", ms: 7 * 24 * 60 * 60 * 1000 },
  { label: "All time", value: "all", ms: 0 },
];

const AGENT_IDS = [
  "agent_1",
  "agent_3",
  "agent_4",
  "agent_5",
  "orchestrator",
];

const severityIcon: Record<string, React.ReactNode> = {
  error: <ErrorOutline sx={{ fontSize: 14 }} />,
  warning: <WarningAmber sx={{ fontSize: 14 }} />,
};

const severityColor: Record<string, "error" | "warning"> = {
  error: "error",
  warning: "warning",
};

function formatTimestamp(creationTime: number): string {
  return new Date(creationTime).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

const columns: GridColDef[] = [
  {
    field: "_creationTime",
    headerName: "Timestamp",
    width: 170,
    sortable: true,
    renderCell: (p: GridRenderCellParams) => (
      <Typography variant="body2" sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
        {formatTimestamp(p.value as number)}
      </Typography>
    ),
  },
  {
    field: "agentId",
    headerName: "Agent",
    width: 130,
    sortable: true,
    renderCell: (p: GridRenderCellParams) => (
      <Chip
        size="small"
        label={p.value}
        variant="outlined"
        sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
      />
    ),
  },
  {
    field: "action",
    headerName: "Action",
    flex: 1,
    minWidth: 160,
    sortable: true,
    renderCell: (p: GridRenderCellParams) => (
      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
        {p.value}
      </Typography>
    ),
  },
  {
    field: "entityType",
    headerName: "Entity",
    width: 120,
    sortable: true,
    renderCell: (p: GridRenderCellParams) => (
      <Chip size="small" label={p.value} variant="outlined" />
    ),
  },
  {
    field: "details",
    headerName: "Details",
    flex: 2,
    minWidth: 240,
    sortable: false,
    renderCell: (p: GridRenderCellParams) => (
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.8rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
        title={p.value as string}
      >
        {p.value}
      </Typography>
    ),
  },
  {
    field: "severity",
    headerName: "Severity",
    width: 120,
    sortable: true,
    renderCell: (p: GridRenderCellParams) => (
      <Chip
        size="small"
        icon={severityIcon[p.value as string] as any}
        label={p.value}
        color={severityColor[p.value as string] ?? "default"}
      />
    ),
  },
];

export function PipelineErrors() {
  const [agentFilter, setAgentFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [timeRange, setTimeRange] = useState("24h");
  const [selectedEntry, setSelectedEntry] = useState<any | null>(null);
  const { cycleArg } = useCycleFilter();

  const data = useStableQuery(api.auditLog.listErrors, {
    ...cycleArg,
    agentId: agentFilter === "all" ? undefined : agentFilter,
    severity: severityFilter === "all" ? undefined : (severityFilter as any),
    limit: 200,
  }, []);

  // Resolve linked documents only when the selected entry points at a
  // transaction or invoice. For other entity types we render details only.
  const txnDocs = useQuery(
    api.transactions.getDocumentsForCompare,
    selectedEntry?.entityType === "transaction"
      ? { id: selectedEntry.entityId as any }
      : "skip",
  );
  const invoiceDocs = useQuery(
    api.invoices.getDocumentsForCompare,
    selectedEntry?.entityType === "invoice"
      ? { id: selectedEntry.entityId as any }
      : "skip",
  );

  // Client-side time range filtering
  const filtered = useMemo(() => {
    if (!data) return [];
    const range = TIME_RANGES.find((r) => r.value === timeRange);
    if (!range || range.ms === 0) return data;
    const cutoff = Date.now() - range.ms;
    return data.filter((entry: any) => entry._creationTime >= cutoff);
  }, [data, timeRange]);

  const allRows: any[] = data ?? [];

  // Live counts so the dropdowns surface what is actually in this scope.
  const agentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const id of AGENT_IDS) counts[id] = 0;
    for (const r of allRows) {
      if (counts[r.agentId] !== undefined) counts[r.agentId]++;
    }
    return counts;
  }, [allRows]);

  const severityCounts = useMemo(() => {
    const counts: Record<string, number> = { error: 0, warning: 0 };
    for (const r of allRows) {
      if (counts[r.severity] !== undefined) counts[r.severity]++;
    }
    return counts;
  }, [allRows]);

  const rows = filtered.map((r: any) => ({ ...r, id: r._id }));

  const resetFilters = () => {
    setAgentFilter("all");
    setSeverityFilter("all");
    setTimeRange("all");
  };

  // Build the drawer config based on the selected entry's entity type.
  const docs = selectedEntry?.entityType === "transaction" ? txnDocs : invoiceDocs;
  const isCompareMode =
    selectedEntry?.entityType === "transaction" ||
    selectedEntry?.entityType === "invoice";

  return (
    <>
      <DataTableShell
        title="Pipeline Errors"
        subtitle="Failed items and warnings across all agents"
        rows={rows}
        columns={columns}
        loading={data === undefined}
        totalBeforeFilters={allRows.length}
        itemLabel="issue"
        emptyTitle="No Pipeline Errors Found"
        emptyDescription="All agents are operating normally. No errors or warnings in the selected time range."
        emptyIcon={<CheckCircleOutline sx={{ fontSize: 32, color: "var(--c-metric-success)" }} />}
        onResetFilters={resetFilters}
        gridProps={{
          initialState: {
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "_creationTime", sort: "desc" }] },
          },
          onRowClick: (params) => setSelectedEntry(params.row),
          sx: { "& .MuiDataGrid-row": { cursor: "pointer" } },
        }}
        filterBar={
          <>
            <CountingFilterSelect
              label="Agent"
              value={agentFilter}
              onChange={setAgentFilter}
              allLabel="All Agents"
              options={AGENT_IDS.map((id) => ({
                value: id,
                label: id,
                count: agentCounts[id],
              }))}
            />
            <CountingFilterSelect
              label="Severity"
              value={severityFilter}
              onChange={setSeverityFilter}
              allLabel="All Severity"
              options={[
                { value: "error", label: "Error", count: severityCounts.error },
                { value: "warning", label: "Warning", count: severityCounts.warning },
              ]}
            />
            <CountingFilterSelect
              label="Time Range"
              value={timeRange}
              onChange={setTimeRange}
              allLabel="All time"
              options={TIME_RANGES.filter((r) => r.value !== "all").map((r) => ({
                value: r.value,
                label: r.label,
              }))}
            />
          </>
        }
      />

      <DocumentCompareDrawer
        open={Boolean(selectedEntry)}
        onClose={() => setSelectedEntry(null)}
        title={selectedEntry?.action ?? "Audit Entry"}
        subtitle={
          selectedEntry
            ? `${selectedEntry.agentId} · ${selectedEntry.entityType} · ${formatTimestamp(selectedEntry._creationTime)}`
            : undefined
        }
        left={
          isCompareMode
            ? {
                storageId: docs?.bankStorageId ?? null,
                label: docs?.bankFileName
                  ? `Bank Statement · ${docs.bankFileName}`
                  : "Bank Statement",
              }
            : undefined
        }
        right={
          isCompareMode
            ? {
                storageId: docs?.invoiceStorageId ?? null,
                label: docs?.invoiceVendorName
                  ? `Invoice · ${docs.invoiceVendorName}${docs.invoiceNumber ? ` #${docs.invoiceNumber}` : ""}`
                  : "Invoice",
              }
            : undefined
        }
        editTarget={
          selectedEntry?.entityType === "transaction"
            ? { type: "transaction", id: selectedEntry.entityId }
            : selectedEntry?.entityType === "invoice"
              ? { type: "invoice", id: selectedEntry.entityId }
              : null
        }
        detailsContent={
          selectedEntry ? (
            <Box>
              <Stack direction="row" spacing={2} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  icon={severityIcon[selectedEntry.severity] as any}
                  label={selectedEntry.severity}
                  color={severityColor[selectedEntry.severity] ?? "default"}
                />
                <Typography variant="caption" color="text.secondary">
                  Entity ID:{" "}
                  <Box component="span" sx={{ fontFamily: "monospace" }}>
                    {selectedEntry.entityId}
                  </Box>
                </Typography>
                {selectedEntry.modelId && (
                  <Typography variant="caption" color="text.secondary">
                    Model: {selectedEntry.modelId}
                  </Typography>
                )}
              </Stack>
              {isCompareMode && <Divider sx={{ my: 1 }} />}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Full Details
              </Typography>
              <DetailsJsonBlock data={selectedEntry.details} />
            </Box>
          ) : null
        }
      />
    </>
  );
}
